import { jsPDF } from "jspdf";
import { markdownToClipboardContent } from "./markdown";

const PAGE_MARGIN = 16;
const DEFAULT_FONT_SIZE = 11;
const DEFAULT_FILE_NAME = "AI-Response.pdf";

const HEADING_STYLES = {
  h1: { fontSize: 18, spaceBefore: 1, spaceAfter: 4 },
  h2: { fontSize: 15, spaceBefore: 5, spaceAfter: 3 },
  h3: { fontSize: 13, spaceBefore: 4, spaceAfter: 2.5 },
  h4: { fontSize: 12, spaceBefore: 3, spaceAfter: 2 },
  h5: { fontSize: 11.5, spaceBefore: 3, spaceAfter: 2 },
  h6: { fontSize: 11, spaceBefore: 2.5, spaceAfter: 2 },
};

function lineHeightMm(fontSizePt) {
  return fontSizePt * 0.42;
}

function sanitizePdfText(text) {
  return String(text ?? "")
    .replace(/\u00a0/g, " ")
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/\u2022/g, "-")
    .replace(/\u00d7/g, "x");
}

function extractHtmlFragment(htmlDoc) {
  const source = String(htmlDoc ?? "");
  const start = source.indexOf("<!--StartFragment-->");
  const end = source.indexOf("<!--EndFragment-->");
  if (start >= 0 && end > start) {
    return source.slice(start + "<!--StartFragment-->".length, end);
  }
  return source;
}

function elementFromClipboardHtml(htmlDoc) {
  const host = document.createElement("div");
  host.innerHTML = extractHtmlFragment(htmlDoc);
  return host;
}

function isElement(node) {
  return node && node.nodeType === Node.ELEMENT_NODE;
}

function fontStyleForRun(run) {
  if (run.bold && run.italic) return "bolditalic";
  if (run.bold) return "bold";
  if (run.italic) return "italic";
  return "normal";
}

function applyRunFont(doc, run, fontSize) {
  doc.setFont(run.code ? "courier" : "helvetica", fontStyleForRun(run));
  doc.setFontSize(fontSize);
}

export function buildAiResponsePdfFileName(markdown) {
  const heading = String(markdown ?? "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .find((line) => /^#{1,6}\s+\S/.test(line));

  const raw = heading ? heading.replace(/^#{1,6}\s+/, "") : DEFAULT_FILE_NAME.replace(/\.pdf$/i, "");
  const safe = sanitizePdfText(raw)
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80);

  return `${safe || "AI-Response"}.pdf`;
}

function collectRuns(node, style = { bold: false, italic: false, code: false }) {
  const runs = [];

  const walk = (current, currentStyle) => {
    if (!current) return;

    if (current.nodeType === Node.TEXT_NODE) {
      const text = sanitizePdfText(current.textContent || "");
      if (text) runs.push({ ...currentStyle, text });
      return;
    }

    if (!isElement(current)) return;

    const tag = current.tagName.toLowerCase();
    if (tag === "br") {
      runs.push({ ...currentStyle, text: "\n" });
      return;
    }
    if (tag === "input" && current.type === "checkbox") {
      runs.push({
        ...currentStyle,
        text: current.checked ? "[x] " : "[ ] ",
      });
      return;
    }

    const nextStyle = { ...currentStyle };
    if (tag === "strong" || tag === "b") nextStyle.bold = true;
    if (tag === "em" || tag === "i") nextStyle.italic = true;
    if (tag === "code") nextStyle.code = true;

    [...current.childNodes].forEach((child) => walk(child, nextStyle));
  };

  walk(node, style);
  return runs;
}

function wrapRuns(doc, runs, maxWidth, fontSize) {
  const lines = [];
  let current = [];
  let currentWidth = 0;

  const pushLine = () => {
    while (current.length && /^\s+$/.test(current[current.length - 1].text)) {
      current.pop();
    }
    lines.push(current);
    current = [];
    currentWidth = 0;
  };

  const measure = (run, text) => {
    applyRunFont(doc, run, fontSize);
    return doc.getTextWidth(text);
  };

  const appendPiece = (run, text) => {
    if (text === "") return;

    let remaining = text;
    while (remaining) {
      let width = measure(run, remaining);
      if (currentWidth + width <= maxWidth || current.length === 0) {
        if (current.length === 0 && /^\s+$/.test(remaining) && remaining !== "\n") {
          return;
        }
        if (current.length === 0 && currentWidth === 0 && width > maxWidth) {
          let fit = remaining;
          while (fit.length > 1 && measure(run, fit) > maxWidth) {
            fit = fit.slice(0, -1);
          }
          current.push({ ...run, text: fit, width: measure(run, fit) });
          currentWidth += measure(run, fit);
          remaining = remaining.slice(fit.length);
          pushLine();
          continue;
        }
        current.push({ ...run, text: remaining, width });
        currentWidth += width;
        return;
      }
      pushLine();
    }
  };

  runs.forEach((run) => {
    const pieces = String(run.text ?? "").split(/(\n|\s+)/);
    pieces.forEach((piece) => {
      if (!piece) return;
      if (piece === "\n") {
        pushLine();
        return;
      }
      appendPiece(run, piece);
    });
  });

  if (current.length) pushLine();
  return lines.filter((line) => line.length > 0 || lines.length === 1);
}

function createPdfWriter() {
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - PAGE_MARGIN * 2;
  let y = PAGE_MARGIN;
  let indent = 0;

  doc.setTextColor(34, 34, 34);
  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.2);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(DEFAULT_FONT_SIZE);

  const usableWidth = () => Math.max(20, contentWidth - indent);

  const addPage = () => {
    doc.addPage();
    y = PAGE_MARGIN;
  };

  const ensureSpace = (needed) => {
    if (y + needed > pageHeight - PAGE_MARGIN) {
      addPage();
    }
  };

  const addSpace = (mm) => {
    if (y <= PAGE_MARGIN + 0.2) return;
    y += mm;
  };

  const drawRunLine = (line, x, baselineY, fontSize) => {
    let cursor = x;
    line.forEach((run) => {
      applyRunFont(doc, run, fontSize);
      doc.setTextColor(34, 34, 34);
      doc.text(run.text, cursor, baselineY);
      cursor += run.width;
    });
  };

  const renderRuns = (runs, { fontSize = DEFAULT_FONT_SIZE, prefix = "" } = {}) => {
    const lh = lineHeightMm(fontSize);
    const width = usableWidth();
    let prefixWidth = 0;
    if (prefix) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(fontSize);
      prefixWidth = doc.getTextWidth(prefix);
    }
    const wrapped = wrapRuns(doc, runs, Math.max(16, width - prefixWidth), fontSize);

    if (!wrapped.length) {
      if (prefix) {
        ensureSpace(lh);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(fontSize);
        doc.text(prefix, PAGE_MARGIN + indent, y + lh * 0.8);
        y += lh;
      }
      return;
    }

    wrapped.forEach((line, index) => {
      ensureSpace(lh);
      const baseline = y + lh * 0.8;
      if (index === 0 && prefix) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(fontSize);
        doc.text(prefix, PAGE_MARGIN + indent, baseline);
      }
      drawRunLine(
        line,
        PAGE_MARGIN + indent + prefixWidth,
        baseline,
        fontSize,
      );
      y += lh;
    });
  };

  const renderPlainText = (text) => {
    const fontSize = DEFAULT_FONT_SIZE;
    const lh = lineHeightMm(fontSize);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(fontSize);
    const lines = doc.splitTextToSize(sanitizePdfText(text), usableWidth());
    lines.forEach((line) => {
      ensureSpace(lh);
      doc.text(line, PAGE_MARGIN + indent, y + lh * 0.8);
      y += lh;
    });
  };

  const renderHeading = (node, tag) => {
    const style = HEADING_STYLES[tag] || HEADING_STYLES.h3;
    addSpace(style.spaceBefore);
    renderRuns(collectRuns(node, { bold: true, italic: tag === "h6", code: false }), {
      fontSize: style.fontSize,
    });
    y += style.spaceAfter;
  };

  const renderParagraph = (node) => {
    const runs = collectRuns(node);
    if (!runs.length) return;
    renderRuns(runs, { fontSize: DEFAULT_FONT_SIZE });
    y += 2.4;
  };

  const renderList = (listNode, level = 0) => {
    const isOl = listNode.tagName.toLowerCase() === "ol";
    const items = [...listNode.children].filter(
      (child) => isElement(child) && child.tagName.toLowerCase() === "li",
    );
    const start = Number(listNode.getAttribute("start") || 1);

    items.forEach((item, index) => {
      const nestedLists = [...item.children].filter((child) => {
        if (!isElement(child)) return false;
        const tag = child.tagName.toLowerCase();
        return tag === "ul" || tag === "ol";
      });
      const clone = item.cloneNode(true);
      [...clone.children].forEach((child) => {
        if (!isElement(child)) return;
        const tag = child.tagName.toLowerCase();
        if (tag === "ul" || tag === "ol") child.remove();
      });

      const prefix = isOl ? `${start + index}. ` : "- ";
      const previousIndent = indent;
      renderRuns(collectRuns(clone), { fontSize: DEFAULT_FONT_SIZE, prefix });
      indent = previousIndent + 6;
      nestedLists.forEach((nested) => renderList(nested, level + 1));
      indent = previousIndent;
      y += 0.6;
    });

    if (level === 0) y += 1.8;
  };

  const renderCodeBlock = (node) => {
    const fontSize = 9;
    const lh = lineHeightMm(fontSize);
    const pad = 2.4;
    const raw = sanitizePdfText(node.innerText || node.textContent || "").replace(/\n$/, "");
    doc.setFont("courier", "normal");
    doc.setFontSize(fontSize);
    const wrapped = [];
    raw.split("\n").forEach((line) => {
      const pieces = doc.splitTextToSize(line.length ? line : " ", usableWidth() - pad * 2);
      wrapped.push(...pieces);
    });

    if (!wrapped.length) wrapped.push("");

    let index = 0;
    while (index < wrapped.length) {
      const available = pageHeight - PAGE_MARGIN - y;
      if (available < lh + pad * 2 && y > PAGE_MARGIN + 0.2) {
        addPage();
        continue;
      }
      const maxLines = Math.max(1, Math.floor((available - pad * 2) / lh));
      const chunk = wrapped.slice(index, index + maxLines);
      const height = chunk.length * lh + pad * 2;
      doc.setFillColor(246, 246, 246);
      doc.setDrawColor(221, 221, 221);
      doc.rect(PAGE_MARGIN + indent, y, usableWidth(), height, "FD");
      doc.setFont("courier", "normal");
      doc.setFontSize(fontSize);
      doc.setTextColor(34, 34, 34);
      chunk.forEach((line, lineIndex) => {
        doc.text(
          line,
          PAGE_MARGIN + indent + pad,
          y + pad + (lineIndex + 0.8) * lh,
        );
      });
      y += height;
      index += chunk.length;
    }
    y += 3;
  };

  const renderTable = (table) => {
    const rows = [...table.querySelectorAll("tr")].map((tr) =>
      [...tr.querySelectorAll("th,td")].map((cell) => ({
        runs: collectRuns(cell, {
          bold: cell.tagName.toLowerCase() === "th",
          italic: false,
          code: false,
        }),
        header: cell.tagName.toLowerCase() === "th",
      })),
    );
    if (!rows.length) return;

    const colCount = Math.max(...rows.map((row) => row.length), 1);
    const normalized = rows.map((row) => {
      const cells = [...row];
      while (cells.length < colCount) {
        cells.push({ runs: [], header: false });
      }
      return cells;
    });

    const fontSize = colCount > 5 ? 8 : colCount > 3 ? 9 : 10;
    const lh = lineHeightMm(fontSize);
    const pad = 1.6;
    const colWidth = usableWidth() / colCount;

    addSpace(2);

    normalized.forEach((cells) => {
      const wrappedCells = cells.map((cell) =>
        wrapRuns(doc, cell.runs, Math.max(8, colWidth - pad * 2), fontSize),
      );
      const lineCount = Math.max(1, ...wrappedCells.map((lines) => lines.length));
      const rowHeight = lineCount * lh + pad * 2;

      if (rowHeight > pageHeight - PAGE_MARGIN * 2) {
        for (let lineIndex = 0; lineIndex < lineCount; lineIndex += 1) {
          ensureSpace(lh + pad);
          const segmentHeight = lh + (lineIndex === 0 || lineIndex === lineCount - 1 ? pad : 0);
          cells.forEach((cell, colIndex) => {
            const x = PAGE_MARGIN + indent + colIndex * colWidth;
            if (cell.header) {
              doc.setFillColor(243, 243, 243);
              doc.rect(x, y, colWidth, segmentHeight, "F");
            }
            doc.setDrawColor(180, 180, 180);
            doc.rect(x, y, colWidth, segmentHeight, "S");
            const line = wrappedCells[colIndex][lineIndex];
            if (line) {
              drawRunLine(line, x + pad, y + segmentHeight - 1.1, fontSize);
            }
          });
          y += segmentHeight;
        }
        return;
      }

      ensureSpace(rowHeight);
      cells.forEach((cell, colIndex) => {
        const x = PAGE_MARGIN + indent + colIndex * colWidth;
        if (cell.header) {
          doc.setFillColor(243, 243, 243);
          doc.rect(x, y, colWidth, rowHeight, "F");
        }
        doc.setDrawColor(180, 180, 180);
        doc.rect(x, y, colWidth, rowHeight, "S");
        wrappedCells[colIndex].forEach((line, lineIndex) => {
          drawRunLine(line, x + pad, y + pad + (lineIndex + 0.8) * lh, fontSize);
        });
      });
      y += rowHeight;
    });

    y += 3;
  };

  const renderBlockquote = (node) => {
    const barX = PAGE_MARGIN + indent + 0.6;
    const startY = y;
    const previousIndent = indent;
    indent += 6;
    [...node.childNodes].forEach(renderNode);
    indent = previousIndent;
    if (y > startY) {
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.7);
      doc.line(barX, startY, barX, y - 1);
      doc.setLineWidth(0.2);
    }
    y += 2;
  };

  const renderNode = (node) => {
    if (!node) return;
    if (node.nodeType === Node.TEXT_NODE) {
      const text = sanitizePdfText(node.textContent || "").trim();
      if (text) renderPlainText(text);
      return;
    }
    if (!isElement(node)) return;

    const tag = node.tagName.toLowerCase();
    switch (tag) {
      case "h1":
      case "h2":
      case "h3":
      case "h4":
      case "h5":
      case "h6":
        renderHeading(node, tag);
        return;
      case "p":
        renderParagraph(node);
        return;
      case "ul":
      case "ol":
        renderList(node);
        return;
      case "pre":
        renderCodeBlock(node);
        return;
      case "table":
        renderTable(node);
        return;
      case "blockquote":
        renderBlockquote(node);
        return;
      case "hr":
        ensureSpace(6);
        addSpace(2);
        doc.setDrawColor(200, 200, 200);
        doc.line(PAGE_MARGIN + indent, y, PAGE_MARGIN + indent + usableWidth(), y);
        y += 4;
        return;
      case "li":
      case "thead":
      case "tbody":
      case "tr":
      case "th":
      case "td":
        [...node.childNodes].forEach(renderNode);
        return;
      default:
        [...node.childNodes].forEach(renderNode);
    }
  };

  return {
    renderRoot(root) {
      [...root.childNodes].forEach(renderNode);
    },
    renderPlainText,
    save(fileName) {
      doc.save(fileName);
    },
  };
}

export async function downloadMarkdownAsPdf(markdown, options = {}) {
  const source = String(markdown ?? "").trim();
  if (!source || typeof document === "undefined") return false;

  const fileName = options.fileName || buildAiResponsePdfFileName(source);
  const writer = createPdfWriter();

  try {
    const { html } = markdownToClipboardContent(source);
    const root = elementFromClipboardHtml(html);
    if (root.childNodes.length) {
      writer.renderRoot(root);
    } else {
      writer.renderPlainText(source);
    }
  } catch {
    writer.renderPlainText(source);
  }

  writer.save(fileName);
  return true;
}
